import "./searchModal.css";
import { debounce } from "lodash";
import {
  boxCategory,
  getBoxCategories,
  boxesSubCategories,
} from "../../api/modules/b2droppie.class";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import BoxListCarousel from "../../components/BoxListCarousel/BoxListCarousel";
import ProductRightSlider from "../ProductRightSlider/ProductRightSlider";

const SearchModal = ({ show, onHide }) => {
  const dropdownRef = useRef(null);
  const observerRef = useRef(null);
  const modalBodyRef = useRef(null);
  const hasMountedRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const [boxData, setBoxData] = useState([]);
  const [category, setCategory] = useState("All");
  const [overlayData, setOverlayData] = useState();
  const [searchQuery, setSearchQuery] = useState("");
  const [showOverlay, setShowOverlay] = useState(false);
  const [boxCategories, setBoxCategories] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortFilter, setSortFilter] = useState({
    filter_by: "box_price",
    order_by: "desc",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    hasMore: true,
  });
  const [subCategories, setSubCategories] = useState([]);
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);

  useEffect(() => {
    if (!show) return;

    setBoxData([]);
    setPagination({
      currentPage: 1,
      lastPage: 1,
      hasMore: true,
    });

    fetchBoxes(
      category,
      searchQuery,
      selectedSubCategories,
      sortFilter,
      1,
      false,
    );
  }, [category, searchQuery, selectedSubCategories, sortFilter, show]);

  useEffect(() => {
    return () => {
      debouncedFetchBoxes.cancel();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!show) {
      setSearchQuery("");
      setCategory("All");
      setBoxData([]);
    }
    if (show) {
      fetchBoxCategories();
      fetchSubCategories();
    }
  }, [show]);

  useEffect(() => {
    const element = observerRef.current;
    const root = modalBodyRef.current;

    if (!element || !root || !pagination.hasMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!hasMountedRef.current) {
          hasMountedRef.current = true;
          return;
        }

        if (
          entry.isIntersecting &&
          pagination.hasMore &&
          !loading &&
          !loadingMoreRef.current
        ) {
          loadingMoreRef.current = true;

          fetchBoxes(
            category,
            searchQuery,
            selectedSubCategories,
            sortFilter,
            pagination.currentPage + 1,
            true,
          );
        }
      },
      {
        root,
        rootMargin: "200px",
        threshold: 0.1,
      },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [
    loading,
    category,
    searchQuery,
    selectedSubCategories,
    sortFilter,
    pagination.currentPage,
    pagination.hasMore,
  ]);

  const fetchBoxCategories = async () => {
    try {
      const response = await getBoxCategories();
      const categoryData = response?.data?.data || [];

      const filtered = categoryData.filter(
        (cat) => cat.name?.toLowerCase() !== "all",
      );

      const categoriesWithAllFirst = [{ id: "all", name: "All" }, ...filtered];

      setBoxCategories(categoriesWithAllFirst);
    } catch (error) {
      console.error("Error fetching box categories:", error);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const response = await boxesSubCategories();
      const data = response?.data?.data || [];
      setSubCategories(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchBoxes = async (
    category,
    searchQuery,
    subCategories,
    sortFilter,
    page = 1,
    append = false,
  ) => {
    setLoading(true);

    try {
      const response = await boxCategory(category, {
        page,
        per_page: 10,
        search: searchQuery,
        subCategories,
        filter_by: sortFilter.filter_by,
        order_by: sortFilter.order_by,
      });

      const boxes = response?.data?.boxes;

      setBoxData((prev) =>
        append ? [...prev, ...(boxes?.data || [])] : boxes?.data || [],
      );

      setPagination({
        currentPage: boxes.current_page,
        lastPage: boxes.last_page,
        hasMore: boxes.current_page < boxes.last_page,
      });
    } finally {
      setLoading(false);
      loadingMoreRef.current = false;
    }
  };

  const debouncedFetchBoxes = useRef(
    debounce((category, search, subCategories, sortFilter) => {
      fetchBoxes(category, search, subCategories, sortFilter);
    }, 400),
  ).current;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      className="search-modal"
      dialogClassName="search-modal-dialog my-0"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body ref={modalBodyRef} className="search-modal-body">
        <div className={`search-container ${isDarkMode ? "dark" : ""}`}>
          {/* TOP BAR */}
          <div className="search-header">
            <div className="search-top">
              <div className="search-filter w-100">
                <div className="modal-search-input w-70">
                  <Search size={18} className="search-icon" />
                  <input
                    value={searchQuery}
                    placeholder="Search"
                    className="search-modal-input-field"
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="filter-dropdown w-30" ref={dropdownRef}>
                  <div
                    className="filter-dropdown-btn"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span>
                      <span className="text-medium-gray">Filter:</span>{" "}
                      {category}
                    </span>
                    <ChevronDown size={16} />
                  </div>

                  {isDropdownOpen && (
                    <div className="filter-dropdown-menu">
                      {boxCategories.map((item, index) => (
                        <button
                          key={index}
                          className="filter-dropdown-item"
                          onClick={() => {
                            setCategory(item.name);
                            setIsDropdownOpen(false);
                          }}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button className="search-modal-close" onClick={onHide}>
                <X />
              </button>
            </div>

            {/* CATEGORIES */}
            <div className="categories">
              {subCategories.map((cat) => (
                <button
                  key={cat.id}
                  className={`category ${
                    selectedSubCategories.includes(cat.id) ? "active" : ""
                  }`}
                  onClick={() =>
                    setSelectedSubCategories((prev) =>
                      prev.includes(cat.id)
                        ? prev.filter((id) => id !== cat.id)
                        : [...prev, cat.id],
                    )
                  }
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
          {/* GRID */}
          <div className="search-content" ref={modalBodyRef}>
            <BoxListCarousel
              hideThis={false}
              searchModal={true}
              cardData={boxData}
              category={category}
              loading={loading}
              hasMore={pagination.hasMore}
              observerRef={observerRef}
              hideSearchModal={onHide}
              setShowOverlay={setShowOverlay}
              setOverlayData={setOverlayData}
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SearchModal;
